/**
 * Criteria Screen (Supplier Eligibility)
 *
 * Evaluates each supplier against configurable thresholds
 * (days trading, invoice count, invoicing months, collections,
 * delinquency ratio, dilution ratio, DSO). The NERB column's
 * classify function combines all threshold checks to flag
 * whether a supplier's entire receivables balance is eligible.
 *
 * Thresholds are derived from the project's linear parameters
 * and compared row-by-row via the `classify` callbacks on each header.
 */
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
// -------------------------------------------------------------
import {
  getLinearParameters,
} from "Redux/data/actions";
import { getCriteriaData } from "Redux/criteria/actions";
// -------------------------------------------------------------
import Number from "Components/Number";
import DataTable from "Components/DataTable";

// -------------------------------------------------------------
// Table header definitions. Each entry can include a `classify`
// function that receives (thresholds, row) and returns true when
// the supplier passes that criterion. DataTable uses this to
// color-code cells (green = pass, red = fail).
// -------------------------------------------------------------

const headers = [
  { text: "Supplier", key: "Supplier", datatype: "string" },
  {
    text: "Days Trading With Buyer",
    key: "Days trading with Buyer",
    datatype: "int",
    classify: (thresholds, row) =>
      row["Days trading with Buyer"] >=
      thresholds["Days Trading With Buyer"].value,
  },
  {
    text: "Invoices Last n Days",
    key: "Invoices Last 90 Days",
    datatype: "int",
    classify: (thresholds, row) =>
      row["Invoices Last 90 Days"] >= thresholds["Invoices Last n Days"].value,
  },
  {
    text: "Invoicing Months In Year",
    key: "Invoicing Months In Year",
    datatype: "int",
    classify: (thresholds, row) =>
      row["Invoicing Months In Year"] >=
      thresholds["Invoicing Months In Year"].value,
  },
  {
    text: "Collections In Year",
    key: "Collections In Year",
    datatype: "int",
    classify: (thresholds, row) =>
      row["Collections In Year"] >= thresholds["Collections In Year"].value,
  },
  {
    text: "Supplier Delinquency Ratio",
    key: "Supplier Delinquency Ratio",
    datatype: "float",
    classify: (thresholds, row) =>
      row["Supplier Delinquency Ratio"] <=
      thresholds["Supplier Delinquency Ratio"].value / 100,
    display: "percentage",
  },
  {
    text: "Supplier Dilution Ratio",
    key: "Supplier Dilution Ratio",
    datatype: "float",
    classify: (thresholds, row) =>
      row["Supplier Dilution Ratio"] <=
      thresholds["Supplier Dilution Ratio"].value / 100,
    display: "percentage",
  },
  {
    text: "DSO",
    key: "Supplier DSO",
    datatype: "float",
    classify: (thresholds, row) =>
      row["Supplier DSO"] <= thresholds["DSO"].value,
  },
  {
    text: "NERB",
    key: "NERB",
    datatype: "float",
    classify: (thresholds, row) =>
      row["Days trading with Buyer"] >=
        thresholds["Days Trading With Buyer"].value &&
      row["Invoices Last 90 Days"] >=
        thresholds["Invoices Last n Days"].value &&
      row["Invoicing Months In Year"] >=
        thresholds["Invoicing Months In Year"].value &&
      row["Collections In Year"] >= thresholds["Collections In Year"].value &&
      row["Supplier Delinquency Ratio"] <=
        thresholds["Supplier Delinquency Ratio"].value / 100 &&
      row["Supplier Dilution Ratio"] <=
        thresholds["Supplier Dilution Ratio"].value / 100 &&
      row["Supplier DSO"] <= thresholds["DSO"].value,
  },
];

const Criteria = () => {
  const dispatch = useDispatch();
  const [thresholds, setThresholds] = useState(null);

  const { criteria } = useSelector((state) => state.criteria);

  const project = useSelector((state) => state.project);

  useEffect(() => {
    dispatch(getCriteriaData(() => {}));
  }, [dispatch]);

  useEffect(() => {
    if (project) {
      const linearParameters = getLinearParameters(project.data);
      setThresholds({
        "Days Trading With Buyer": {
          value: parseInt(linearParameters.min_days_trading_with_buyer.value),
          unit: linearParameters.min_days_trading_with_buyer.unit,
        },
        "Invoices Last n Days": {
          value: parseInt(linearParameters.min_number_of_invoices.value),
          unit: linearParameters.min_number_of_invoices.unit,
        },
        "Invoicing Months In Year": {
          value: parseInt(
            linearParameters.min_invoicing_months_in_period.value
          ),
          unit: linearParameters.min_invoicing_months_in_period.unit,
        },
        "Collections In Year": {
          value: parseInt(linearParameters.collections_in_period.value),
          unit: linearParameters.collections_in_period.unit,
        },
        "Supplier Delinquency Ratio": {
          value: parseFloat(linearParameters.delinquency_threshold.value),
          unit: linearParameters.delinquency_threshold.unit,
        },
        "Supplier Dilution Ratio": {
          value: parseFloat(linearParameters.dilution_threshold.value),
          unit: linearParameters.dilution_threshold.unit,
        },
        DSO: {
          value: parseFloat(linearParameters.dso.value),
          unit: linearParameters.dso.unit,
        },
      });
    }
  }, [project]);

  if (!project.hasMaster)
    return (
      <div className="notification is-warning is-light">
        No master frame has been built yet.
      </div>
    );

  return (
    <div>
      {criteria && thresholds && (
        <DataTable
          data={criteria.data}
          headers={headers}
          reference="Supplier"
          thresholds={thresholds}
        />
      )}
    </div>
  );
};

export default Criteria;

/** Renders a value tag color-coded green/red based on a threshold comparison (currently unused). */
const Value = ({ threshold, value, dir }) => { // eslint-disable-line no-unused-vars
  if (!threshold)
    return (
      <div className="tag is-small">
        <Number value={value} />
      </div>
    );
  const condition =
    dir === "negative" ? value <= threshold : value >= threshold;
  if (condition) {
    return (
      <div className="tag is-small is-success is-light">
        <Number value={value} />
      </div>
    );
  }
  return (
    <div className="tag is-small is-danger is-light">
      <Number value={value} />
    </div>
  );
};

const Threshold = ({ value }) => { // eslint-disable-line no-unused-vars
  return <div className="tag is-primary is-light">{value}</div>;
};

const NERB = ({ value, condition }) => { // eslint-disable-line no-unused-vars
  if (condition) {
    return (
      <div className="tag is-success ">
        <Number value={value} />
      </div>
    );
  }
  return (
    <div className="tag is-small is-danger is-light">
      <Number value={value} />
    </div>
  );
};
